import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (F901)
// ============================================================

export interface F9011 {
  f9011_01: string;         // 員工代號
  f9011_02: number;         // 序號
  f9011_11: string;         // 姓名
  f9011_12: string;         // 關係
  f9011_13: string | null;  // 生日
  f9011_14: string;         // 身份證號
}

export interface F9012 {
  f9012_01: string;         // 員工代號
  f9012_02: number;         // 序號
  f9012_11: string;         // 公司名稱
  f9012_12: string;         // 職稱
  f9012_13: string | null;  // 起始日
  f9012_14: string | null;  // 結束日
  f9012_15: string;         // 工作內容
}

export interface F9013 {
  f9013_01: string;         // 員工代號
  f9013_02: number;         // 序號
  f9013_11: string;         // 證照名稱
  f9013_12: string;         // 發照單位
  f9013_13: string | null;  // 取得日期
  f9013_14: string | null;  // 有效期限
  f9013_15: string;         // 證照編號
}

export interface F9014 {
  f9014_01: string;         // 員工代號
  f9014_02: number;         // 序號
  f9014_11: string;         // 部門代號
  f9014_11a: string;        // 部門名稱
  f9014_12: string | null;  // 設定日期
}

export interface F901 {
  f901_01: string;          // 員工代號 (PK)
  f901_11: string;          // 姓名
  f901_12: string;          // 身份證號
  f901_13: string | null;   // 生日
  f901_14: number;          // 性別
  f901_15: number;          // 學歷
  f901_16: string;          // 最後就讀
  f901_17: string;          // 科系
  f901_171: number;         // 年制
  f901_172: string;         // 日/夜間部
  f901_18: number;          // 畢業年度
  f901_19: string;          // 聯絡電話
  f901_20: string;          // 行動電話
  f901_25: string;          // 聯絡地址
  f901_26: string;          // 戶籍地址
  f901_27: string;          // 緊急聯絡人
  f901_28: string;          // 緊急電話
  f901_29: string;          // 血型
  f901_30: string;          // 公司名稱
  f901_31: string | null;   // 到職日
  f901_311: string | null;  // 年資起算
  f901_312: string;         // 說明
  f901_32: string;          // 部門
  f901_33: string;          // 職稱
  f901_34: string | null;   // 離職日
  f901_35: string | null;   // 轉正日
  f901_37: string;          // 職等
  f901_371: string;         // 核准等級
  f901_38: string;          // 籍貫
  f901_40: number;          // 婚姻狀況
  f901_42: number;          // 身高
  f901_43: number;          // 體重
  f901_441: boolean;        // 機車執照
  f901_442: boolean;        // 汽車執照
  f901_44a: boolean;        // 交通車
  f901_44b: boolean;        // 住宿
  f901_481: string;         // 健康狀況
  f901_49: boolean;         // 須刷卡
  f901_51: string;          // 上班組別
  f901_52: string;          // 郵件地址
  f901_55: string;          // 英文名字
  f901_56: string;          // 輪休組別
  f901_60: boolean;         // 正式員工
  f901_64: number;          // 家庭成員數
  f901_65: number;          // 工作經歷數
  f901_66: number;          // 證照資格數
  f901_69: number;          // 證照附件數
  f901_70: boolean;         // 人事負責人
  f901_72: string;          // 權限代理人
  f901_91: string;          // 建立人員代號
  f901_92: string | null;   // 建立日期
  f901_93: string;          // 最後異動人員代號

  // Sub-tables
  details_9011?: F9011[];
  details_9012?: F9012[];
  details_9013?: F9013[];
  details_9014?: F9014[];
}

export interface ListResponse {
  data: F901[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  code: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

const BASE = '/api/employee';

const employeeApi = {
  list: async (page = 1, size = 100, keyword?: string): Promise<ListResponse> => {
    if (keyword) {
      const res = await apiClient.get(`${BASE}/search`, { params: { keyword, page, size } });
      return res.data;
    }
    const res = await apiClient.get(BASE, { params: { page, size } });
    return res.data;
  },

  search: async (keyword: string, page = 1, size = 20): Promise<ListResponse> => {
    const res = await apiClient.get(`${BASE}/search`, { params: { keyword, page, size } });
    return res.data;
  },

  get: async (code: string): Promise<F901> => {
    const res = await apiClient.get(`${BASE}/${encodeURIComponent(code)}`);
    return res.data;
  },

  create: async (data: Partial<F901>): Promise<F901> => {
    const res = await apiClient.post(BASE, data);
    return res.data;
  },

  update: async (code: string, data: Partial<F901>): Promise<F901> => {
    const res = await apiClient.put(`${BASE}/${encodeURIComponent(code)}`, data);
    return res.data;
  },

  delete: async (code: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${encodeURIComponent(code)}`);
  },
};

export default employeeApi;
